import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Navigate } from "react-router-dom";
import { useAuthContext } from "./AuthContext";
import { get } from "lodash";
import { createPortal } from "react-dom";
import { useLazyQuery } from "@apollo/client";
import { SpinnerView } from "../elements/Spinners";
import { FETCH_COMPANY_BY_ID } from "../queries/organizations";

// Import utility functions
import {
  PersonaType,
  getStorageKey,
  getSelectedEntityId,
  setSelectedEntityId,
  shouldShowModal,
  dismissModal,
  getPendingClaimsModalKey,
  getNoClaimsModalKey,
} from "./utils/storage";
import { canClaimOrganization } from "./utils/claims";
import {
  Preference,
  extractPreferences,
  extractMergedPreferences,
} from "./utils/preferences";
import { getCurrentPersona } from "../utils/persona";

export type { Preference, PersonaType };

export type ApplicationContextType = {
  // User & Persona
  user?: any;
  persona: PersonaType;

  // Claims (only for FOUNDER/INVESTOR/ACCELERATOR)
  claims: any[]; // All claims from user
  claims_valid: any[]; // Approved only (status === 1)
  claims_approved: any[]; // Approved only (status === 1)
  claims_pending: any[]; // Pending only (status === 0)
  claims_rejected: any[]; // Rejected only (status === -1)
  currentClaim?: any;
  setCurrentOrganization: (orgId: string) => void;

  // Organization/Entity
  organization?: any;
  current?: any;

  // Preferences (all personas)
  preferences: Preference[];

  // Loading
  loading: boolean;
};

const ApplicationContext = createContext<ApplicationContextType | undefined>(
  undefined,
);

interface ApplicationContextProviderProps {
  children: ReactNode;
  persona?: PersonaType;
}

export const ApplicationContextProvider = ({
  children,
  persona: propPersona,
}: ApplicationContextProviderProps) => {
  const { user, userLoading } = useAuthContext();

  // Determine persona using getCurrentPersona (supports localStorage for Briter users)
  const [persona, setPersona] = useState<PersonaType>(() =>
    getCurrentPersona(user) as PersonaType
  );

  // Minimal state - only store what we fetch
  const [organization, setOrganization] = useState<any>(null);
  const [currentClaim, setCurrentClaim] = useState<any>(null);

  // Sync persona with user changes (when user loads or updates)
  useEffect(() => {
    if (user) {
      const currentPersona = getCurrentPersona(user) as PersonaType;
      if (currentPersona !== persona) {
        setPersona(currentPersona);
      }
    }
  }, [user, persona]);

  // Listen for persona changes from other tabs/windows (localStorage event)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "current_persona" && user) {
        const newPersona = getCurrentPersona(user) as PersonaType;
        setPersona(newPersona);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [user]);

  // Listen for persona changes from same tab (custom event from NavBar)
  useEffect(() => {
    const handlePersonaChange = () => {
      if (user) {
        const newPersona = getCurrentPersona(user) as PersonaType;
        setPersona(newPersona);
      }
    };

    window.addEventListener("personaChanged", handlePersonaChange);
    return () =>
      window.removeEventListener("personaChanged", handlePersonaChange);
  }, [user]);

  // Compute derived values from user (memoized to prevent infinite loops)
  const claims = useMemo(() => get(user, "claims", []), [user]);
  const claims_valid = useMemo(
    () => claims.filter((claim: any) => claim.status === 1),
    [claims]
  );
  const claims_approved = useMemo(
    () => claims.filter((claim: any) => claim.status === 1),
    [claims]
  );
  const claims_pending = useMemo(
    () => claims.filter((claim: any) => claim.status === 0),
    [claims]
  );
  const claims_rejected = useMemo(
    () => claims.filter((claim: any) => claim.status === -1),
    [claims]
  );

  // Check if this persona can claim organizations
  const hasClaimFeature = canClaimOrganization(persona);
  const hasPendingClaims = hasClaimFeature && claims_pending.length > 0;

  // Check if user has briterbridges.com email
  const isBriterBridgesUser = get(user, "email", "").endsWith(
    "@briterbridges.com"
  );

  // Compute preferences from user metadata + organization (memoized)
  const preferences = useMemo(
    () =>
      organization
        ? extractMergedPreferences(get(user, "metadata", {}), organization)
        : extractPreferences(get(user, "metadata", {}), "user"),
    [user, organization]
  );

  // Current claim with organization data (memoized)
  const current = useMemo(
    () => (currentClaim ? { ...currentClaim, organization } : undefined),
    [currentClaim, organization]
  );

  // Migrate old FundingKit localStorage keys to FOUNDER persona keys (one-time migration)
  useEffect(() => {
    if (persona === "FOUNDER") {
      const oldEntityId = localStorage.getItem("fundingkit_selected_entity_id");
      const oldPendingClosed = localStorage.getItem(
        "fundingkit_pending_claims_closed_at"
      );
      const oldNoClosed = localStorage.getItem(
        "fundingkit_no_claims_closed_at"
      );

      if (oldEntityId) {
        setSelectedEntityId("FOUNDER", oldEntityId);
        localStorage.removeItem("fundingkit_selected_entity_id");
      }
      if (oldPendingClosed) {
        localStorage.setItem("founder_pending_claims_closed_at", oldPendingClosed);
        localStorage.removeItem("fundingkit_pending_claims_closed_at");
      }
      if (oldNoClosed) {
        localStorage.setItem("founder_no_claims_closed_at", oldNoClosed);
        localStorage.removeItem("fundingkit_no_claims_closed_at");
      }
    }
  }, [persona]);

  const [isPendingClaimsModalOpen, setIsPendingClaimsModalOpen] = useState(
    () => shouldShowModal(getPendingClaimsModalKey(persona))
  );
  const [isNoClaimsModalOpen, setIsNoClaimsModalOpen] = useState(
    () => shouldShowModal(getNoClaimsModalKey(persona))
  );

  const [fetchCompany, { loading }] = useLazyQuery(FETCH_COMPANY_BY_ID);

  // Close handlers that save timestamp to localStorage
  const closePendingClaimsModal = () => {
    dismissModal(getPendingClaimsModalKey(persona));
    setIsPendingClaimsModalOpen(false);
  };

  const closeNoClaimsModal = () => {
    dismissModal(getNoClaimsModalKey(persona));
    setIsNoClaimsModalOpen(false);
  };

  const setCurrentOrganization = (orgId: string) => {
    if (!hasClaimFeature) return;

    const selectedClaim = claims.find(
      (claim: any) => get(claim, "organization.id") === orgId
    );

    if (!selectedClaim) return;

    // Save selected organization to localStorage with persona-scoped key
    setSelectedEntityId(persona, orgId);

    fetchCompany({
      variables: {
        id: orgId,
      },
    }).then((res) => {
      const organizationData = get(res, "data.organization", {});
      setOrganization(organizationData);
      setCurrentClaim(selectedClaim);
    });
  };

  // Reset modal states when claims change (only if 24h has passed)
  useEffect(() => {
    if (!hasClaimFeature) return;

    if (shouldShowModal(getPendingClaimsModalKey(persona))) {
      setIsPendingClaimsModalOpen(true);
    }
    if (shouldShowModal(getNoClaimsModalKey(persona))) {
      setIsNoClaimsModalOpen(true);
    }
  }, [hasPendingClaims, claims.length, hasClaimFeature, persona]);

  useEffect(() => {
    if (user && hasClaimFeature) {
      // FOUNDER/INVESTOR/ACCELERATOR - Claims-based logic
      const nonRejectedClaims = claims.filter((c: any) => c.status !== -1);

      // Try to get saved organization from localStorage
      const savedOrgId = getSelectedEntityId(persona);

      // Find the saved organization in non-rejected claims
      const savedClaim = savedOrgId
        ? nonRejectedClaims.find(
            (claim: any) => get(claim, "organization.id") === savedOrgId
          )
        : null;

      // Determine which organization to use
      let selectedOrgId: string | undefined;
      let selectedClaim: any;

      if (savedClaim && savedOrgId) {
        // Use saved organization if it exists in non-rejected claims
        selectedOrgId = savedOrgId;
        selectedClaim = savedClaim;
      } else {
        // Priority: 1) First approved claim, 2) First pending claim
        if (claims_approved.length > 0) {
          selectedOrgId = get(claims_approved[0], "organization.id");
          selectedClaim = claims_approved[0];
        } else if (claims_pending.length > 0) {
          selectedOrgId = get(claims_pending[0], "organization.id");
          selectedClaim = claims_pending[0];
        }

        // Save the selected organization
        if (selectedOrgId) {
          setSelectedEntityId(persona, selectedOrgId);
        }
      }

      if (selectedOrgId) {
        fetchCompany({
          variables: {
            id: selectedOrgId,
          },
        }).then((res) => {
          const organizationData = get(res, "data.organization", {});
          setOrganization(organizationData);
          setCurrentClaim(selectedClaim);
        });
      } else {
        // No approved or pending claims
        setOrganization(null);
        setCurrentClaim(null);
      }
    }
  }, [fetchCompany, user, persona, hasClaimFeature, claims_approved, claims_pending]);

  // Redirect to onboarding if user has no approved or pending claims (only rejected or none)
  if (
    hasClaimFeature &&
    !userLoading &&
    !loading &&
    claims_approved.length === 0 &&
    claims_pending.length === 0
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <ApplicationContext.Provider
      value={{
        user,
        persona,
        claims,
        claims_valid,
        claims_approved,
        claims_pending,
        claims_rejected,
        currentClaim,
        organization,
        current,
        preferences,
        setCurrentOrganization,
        loading,
      }}
    >
      {/* Show loading spinner while fetching organization */}
      {loading && <SpinnerView message="Loading organization..." />}

      {/* Show modal for pending claims (only for claim-based personas, except briterbridges.com users) */}
      {hasClaimFeature &&
        !userLoading &&
        !loading &&
        hasPendingClaims &&
        !isBriterBridgesUser &&
        isPendingClaimsModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999]"
            onClick={closePendingClaimsModal}
          >
            <div
              className="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4 relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={closePendingClaimsModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>

              <div className="flex flex-col items-center text-center">
                {/* Loading spinner icon */}
                <div className="mb-6">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-ab-lightdarkgreen"></div>
                </div>

                {/* Title */}
                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                  Verification in Progress
                </h2>

                {/* Message */}
                <p className="text-gray-600 mb-4">
                  We are currently reviewing your organisation claim request.
                  This typically takes less than 24 hours.
                </p>

                <p className="text-sm text-gray-500">
                  We will notify you once the verification is complete. <br />{" "}
                  Thank you for your patience.
                </p>

                {/* Optional: Add a note about email notification */}
                <div className="mt-6 p-3 bg-ab-lightteal rounded-md w-full">
                  <p className="text-xs text-ab-darkergreen">
                    You will receive an email notification at{" "}
                    <span className="font-semibold">
                      {get(user, "email", "your registered email")}
                    </span>{" "}
                    once your request has been processed.
                  </p>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Show modal for no claims (only for claim-based personas) */}
      {hasClaimFeature &&
        !userLoading &&
        !loading &&
        claims_approved.length === 0 &&
        claims_pending.length === 0 &&
        isNoClaimsModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 bg-white bg-opacity-75 flex justify-center items-start z-50"
            onClick={closeNoClaimsModal}
          >
            <div
              className="mt-12 bg-white rounded-lg shadow-md border p-6 w-full max-w-5xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={closeNoClaimsModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
                aria-label="Close modal"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>

              <div className="p-6 text-center">
                <h2 className="text-2xl font-semibold mb-4">
                  No Organization Claims Found
                </h2>
                <p className="text-gray-600 mb-6">
                  To get started, please claim your organization. This will allow you to
                  access personalized insights and data.
                </p>
                <p className="text-sm text-gray-500">
                  Contact support if you need assistance with claiming your organization.
                </p>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {!loading && children}
    </ApplicationContext.Provider>
  );
};

export const useApplicationContext = () => {
  const context = useContext(ApplicationContext);
  if (!context) {
    throw new Error(
      "useApplicationContext must be used within ApplicationContextProvider",
    );
  }
  return context;
};
